/**
 * Convert a nominal wage to real purchasing power using BEA Regional Price Parity.
 * Formula matches api-salary / api-top-occupations: amount × (100 / rppIndex).
 */
export function applyRpp(amount: number, rppIndex: number): number {
  if (!amount || !Number.isFinite(amount)) return 0;
  if (!rppIndex || !Number.isFinite(rppIndex) || rppIndex <= 0) return Math.round(amount);
  return Math.round(amount * (100 / rppIndex));
}

/**
 * Hourly wages keep two decimal places (same as api-salary).
 */
export function applyRppHourly(amount: number, rppIndex: number): number {
  if (!amount || !Number.isFinite(amount)) return 0;
  if (!rppIndex || !Number.isFinite(rppIndex) || rppIndex <= 0) {
    return Math.round(amount * 100) / 100;
  }
  return Math.round(amount * (100 / rppIndex) * 100) / 100;
}

/**
 * 2024 state RPPs from the BEA Regional API (SARPP, all items) — used only
 * when the live get_area_rpp RPC is unreachable (network failure, missing
 * migration). Refresh alongside the annual ingest-bea-rpp update.
 */
export const STATE_RPP_FALLBACK: Record<string, number> = {
  '01': 88.823,
  '02': 102.359,
  '04': 100.677,
  '05': 86.937,
  '06': 110.72,
  '08': 103.052,
  '09': 103.61,
  '10': 99.808,
  '11': 109.901,
  '12': 103.414,
  '13': 96.293,
  '15': 109.951,
  '16': 95.494,
  '17': 99.958,
  '18': 93.329,
  '19': 87.762,
  '20': 90.068,
  '21': 90.159,
  '22': 88.207,
  '23': 97.05,
  '24': 104.959,
  '25': 105.757,
  '26': 96.217,
  '27': 98.621,
  '28': 86.953,
  '29': 90.817,
  '30': 94.645,
  '31': 90.103,
  '32': 99.979,
  '33': 104.165,
  '34': 108.805,
  '35': 92.212,
  '36': 107.921,
  '37': 94.326,
  '38': 88.959,
  '39': 92.774,
  '40': 87.843,
  '41': 103.361,
  '42': 97.572,
  '44': 102.28,
  '45': 93.749,
  '46': 88.586,
  '47': 91.87,
  '48': 97.057,
  '49': 98.864,
  '50': 97.958,
  '51': 101.104,
  '53': 107.013,
  '54': 89.497,
  '55': 94.095,
  '56': 92.691,
};

export function getFallbackStateRpp(oewsAreaCode: string): number | null {
  if (!/^\d{2}$/.test(oewsAreaCode)) return null;
  return STATE_RPP_FALLBACK[oewsAreaCode] ?? null;
}
